import { Box, Typography } from "@mui/material";
import confetti from "canvas-confetti";
import { useEffect, useRef } from "react";
import {
  icon50Scores,
  iconBBIW,
  iconDot,
  iconEconomy,
  iconFourWickets,
  iconHS,
  iconInnings,
  iconMatches,
  iconOvers,
  iconRuns,
  iconSR,
  profile,
  profile2,
} from "../../../Assets";
import {
  getAuctionRulesByDivision,
  normalizeCategoryForRules,
  resolveAuctionDivisionFromHints,
  resolveCategoryWithFallback,
  type AuctionDivision,
} from "../../../Utils/auctionRules";
import {
  formatDOB,
  mapHighestLevelToCategory,
} from "../../../Utils/helperFunctions";
import {
  compactCurrency,
  getPlayerName,
  getPlayerPhoto,
  isIconPlayer,
  stripe,
} from "./helpers";

type PlayerPreviewProps = {
  player: any;
  liveBidData: any;
  division: AuctionDivision;
  divisionHint?: AuctionDivision;
};

export const PlayerPreview = ({
  player,
  liveBidData,
  division,
  divisionHint,
}: PlayerPreviewProps) => {
  const effectiveDivision: AuctionDivision = resolveAuctionDivisionFromHints({
    gender: player?.gender,
    divisionHint: divisionHint || division,
  });
  const isDirectIconAllocation =
    isIconPlayer(player) && effectiveDivision !== "women";
  const confettiCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isLiveBidding = liveBidData?.status === "liveBid";
  const fullName = getPlayerName(player);
  const city = player?.city || player?.currentCity || "Mumbai";
  const mcaNo = player?.MCANo || "-";
  const resolvedMcaNo =
    player?.MCANo ||
    player?.MCA_No__c ||
    player?.MCA_Registration_Number__c ||
    player?.Player__r?.MCA_No__c ||
    player?.Player__r?.MCA_Registration_Number__c;
  const role = player?.roleType || player?.role || "-";
  const dobText = player?.playerBirthDate ? formatDOB(player.playerBirthDate) : "-";
  const playerAgeValue =
    player?.playerAge ??
    player?.age ??
    player?.Player__r?.Age__c ??
    player?.Age__c;
  const playerAgeNumber =
    typeof playerAgeValue === "number"
      ? playerAgeValue
      : Number.parseFloat(String(playerAgeValue ?? ""));
  const showUnder19Tag =
    Number.isFinite(playerAgeNumber) && playerAgeNumber < 19;
  const rules = getAuctionRulesByDivision(effectiveDivision);
  const category =
    resolveCategoryWithFallback({
      division: effectiveDivision,
      mcaNo: resolvedMcaNo,
      fallbackCategory:
        player?.playerCategory ||
        normalizeCategoryForRules(
          mapHighestLevelToCategory(player?.highest_level_played) || "",
          rules,
          {
            playerAge: player?.playerAge,
            birthData: player?.playerBirthDate || player?.birthData,
          },
        ) ||
        "",
    }) ||
    "-";
  const basePrice = compactCurrency(player?.basePrice);
  const auctionResult = player?.auctionResult;
  const bidAmount = isLiveBidding
    ? compactCurrency(liveBidData?.bidAmount)
    : compactCurrency(player?.bidAmount);
  const soldBidAmountDisplay =
    auctionResult === "Sold" &&
    bidAmount &&
    !/[a-z]/i.test(bidAmount)
      ? `${bidAmount}L`
      : bidAmount;
  const soldLogo =
    typeof (player?.teamLogo || player?.soldToLogo) === "string"
      ? (player?.teamLogo || player?.soldToLogo).trim()
      : "";
  const t20Batting = Array.isArray(player?.playerDetails?.stats)
    ? player.playerDetails.stats.find(
        (stat: any) =>
          stat?.matchType === "T20" &&
          `${stat?.recordType || ""}`.toLowerCase() === "batting",
      )
    : undefined;
  const t20Bowling = Array.isArray(player?.playerDetails?.stats)
    ? player.playerDetails.stats.find(
        (stat: any) =>
          stat?.matchType === "T20" &&
          `${stat?.recordType || ""}`.toLowerCase() === "bowling",
      )
    : undefined;
  const roleText = `${player?.role || ""} ${player?.roleType || ""}`.toLowerCase();
  const isAllRounder =
    roleText.includes("all-rounder") ||
    roleText.includes("all - rounder") ||
    roleText.includes("all rounder");
  const isBowlerRole =
    (roleText.includes("bowler") || roleText.includes("bowling")) &&
    !isAllRounder;
  const inningsValue = isBowlerRole
    ? t20Bowling?.innings ?? "-"
    : t20Batting?.innings ?? t20Bowling?.innings ?? "-";
  const battingStatItems = [
    {
      label: "Matches",
      value: t20Batting?.matches ?? player?.matches ?? player?.Matches__c ?? "-",
      icon: iconMatches,
    },
    {
      label: "Average",
      value:
        t20Batting?.batting?.average ??
        player?.average ??
        player?.Average__c ??
        "-",
      icon: iconSR,
    },
    {
      label: "Innings",
      value: t20Batting?.innings ?? player?.innings ?? player?.Innings__c ?? "-",
      icon: iconInnings,
    },
    {
      label: "50+ Scores",
      value:
        t20Batting?.batting?.fifties ??
        player?.fiftyScores ??
        player?.Fifty_Plus__c ??
        "-",
      icon: icon50Scores,
    },
    {
      label: "Runs",
      value: t20Batting?.totalRuns ?? player?.runs ?? player?.Runs__c ?? "-",
      icon: iconRuns,
    },
    {
      label: "HS",
      value:
        t20Batting?.batting?.highScore ??
        player?.highestScore ??
        player?.HS__c ??
        "-",
      icon: iconHS,
    },
    {
      label: "SR",
      value: t20Batting?.strikeRate ?? player?.strikeRate ?? player?.SR__c ?? "-",
      icon: iconSR,
    },
  ];
  const bowlingCoreStatItems = [
    {
      label: "Overs",
      value: t20Bowling?.bowling?.oversBowled ?? player?.overs ?? "-",
      icon: iconOvers,
    },
    {
      label: "Total runs conceded",
      value:
        t20Bowling?.bowling?.totalRunsConceeded ??
        player?.totalRunsConceded ??
        "-",
      icon: iconRuns,
    },
    {
      label: "Wickets",
      value:
        t20Bowling?.bowling?.wickets ??
        t20Bowling?.totalwickets ??
        player?.wickets ??
        "-",
      icon: iconMatches,
    },
    {
      label: "Dot%",
      value: t20Bowling?.bowling?.dotBallPercentage ?? player?.dotPercentage ?? "-",
      icon: iconDot,
    },
    {
      label: "Economy",
      value: t20Bowling?.bowling?.economyRate ?? player?.economyRate ?? "-",
      icon: iconEconomy,
    },
    {
      label: "BBIW",
      value: t20Bowling?.bowling?.bbiw ?? player?.bbiw ?? "-",
      icon: iconBBIW,
    },
    {
      label: "Fourwickets",
      value: t20Bowling?.bowling?.fourWickets ?? player?.fourWickets ?? "-",
      icon: iconFourWickets,
    },
  ];
  const bowlingStatItems = [
    { label: "Innings", value: t20Bowling?.innings ?? "-", icon: iconInnings },
    ...bowlingCoreStatItems,
  ];
  const statItems = isAllRounder
    ? [...battingStatItems, ...bowlingCoreStatItems]
    : isBowlerRole
      ? bowlingStatItems
      : battingStatItems;
  const isDenseStats = statItems.length >= 12 || isAllRounder;

  useEffect(() => {
    const canvas = confettiCanvasRef.current;
    if (
      auctionResult !== "Sold" ||
      isLiveBidding ||
      !(canvas instanceof HTMLCanvasElement) ||
      !canvas.isConnected ||
      typeof canvas.getContext !== "function" ||
      typeof canvas.getBoundingClientRect !== "function"
    ) {
      return;
    }
    const bounds = canvas.getBoundingClientRect();
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
      return;
    }
    canvas.width = Math.max(1, Math.floor(bounds.width));
    canvas.height = Math.max(1, Math.floor(bounds.height));

    let interval: ReturnType<typeof setInterval> | null = null;
    let myConfetti: ReturnType<typeof confetti.create> | null = null;

    try {
      myConfetti = confetti.create(canvas, {
        resize: false,
        useWorker: true,
      });
      const duration = 60 * 1000;
      const animationEnd = Date.now() + duration;

      interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          if (interval) clearInterval(interval);
          return;
        }

        if (myConfetti) {
          myConfetti({
            particleCount: 60,
            spread: 70,
            origin: {
              x: Math.random(),
              y: Math.random() - 0.2,
            },
          });
        }
      }, 300);
    } catch (error) {
      console.error("Confetti initialization failed:", error);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (myConfetti && typeof myConfetti.reset === "function") {
        myConfetti.reset();
      }
    };
  }, [auctionResult, isLiveBidding, player?.Id]);

  const statusPanel = (() => {
    if (auctionResult === "Sold" && !isLiveBidding) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            background:
              "linear-gradient(90deg, rgba(255, 64, 150, 0.28) 0%, rgba(80, 11, 49, 0.62) 52%, rgba(0,0,0,0.78) 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            <canvas
              ref={confettiCanvasRef}
              style={{ width: "100%", height: "100%", display: "block" }}
            />
          </Box>
          <Typography
            sx={{
              position: "relative",
              zIndex: 2,
              width: "100%",
              px: { xs: 1.1, md: "0.9vw" },
              py: { xs: 0.55, md: "0.42vw" },
              color: "#fff",
              fontSize: { xs: "0.85rem", md: "1.25vw" },
              fontWeight: 900,
              background:
                "linear-gradient(90deg, rgba(255, 64, 150, 0.95) 0%, rgba(122, 17, 74, 0.72) 62%, rgba(32, 6, 18, 0.2) 100%)",
            }}
          >
            SOLD
          </Typography>

          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              flex: 1,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: { xs: 1.1, md: "1.2vw" },
            }}
          >
            <Typography
              sx={{
                color: "#ffe31f",
                fontWeight: 900,
                fontSize: { xs: "2.1rem", sm: "3rem", md: "4.05vw" },
                lineHeight: 1,
                letterSpacing: "-0.02em",
              }}
            >
              {soldBidAmountDisplay}
            </Typography>
            {soldLogo ? (
              <Box sx={{ pr: { xs: 2, md: "5vw" } }}>
                <img
                  src={soldLogo}
                  alt="Sold team"
                  style={{
                    width: "7.2vw",
                    minWidth: 58,
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : null}
          </Box>

          <Typography
            sx={{
              position: "relative",
              zIndex: 2,
              width: "100%",
              color: "#fff",
              fontWeight: 700,
              fontSize: { xs: "0.9rem", md: "2vw" },
              px: { xs: 1.1, md: "0.9vw" },
              py: { xs: 0.52, md: "0.42vw" },
              background:
                "linear-gradient(90deg, rgba(255, 72, 157, 0.55) 0%, rgba(92, 19, 58, 0.44) 66%, rgba(20, 8, 15, 0.12) 100%)",
            }}
          >
            BASE PRICE : {basePrice}
          </Typography>
        </Box>
      );
    }

    if (auctionResult === "Unsold" && !isLiveBidding) {
      return (
        <Box
          sx={{
            flex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            background:
              "linear-gradient(90deg, rgba(172,38,102,0.72) 0%, rgba(45,8,31,0.54) 58%, rgba(0,0,0,0.28) 100%)",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 900,
              fontStyle: "italic",
              fontSize: { xs: "2.25rem", sm: "3.2rem", md: "4.2vw" },
              lineHeight: 1,
              flex: 1,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            UNSOLD
          </Typography>
          <Typography
            sx={{
              width: "100%",
              textAlign: "center",
              color: "#fff",
              fontWeight: 800,
              fontSize: { xs: "0.9rem", md: "2vw" },
              py: { xs: 1, md: "0.7vw" },
              background: "rgba(255,255,255,0.08)",
            }}
          >
            BASE PRICE : {basePrice}
          </Typography>
        </Box>
      );
    }

    if (isDirectIconAllocation && auctionResult !== "Sold") {
      return (
        <Box
          sx={{
            flex: 1,
            height: "100%",
            minWidth: 0,
            width: "100%",
            boxSizing: "border-box",
            background:
              "linear-gradient(90deg, rgba(172,38,102,0.76) 0%, rgba(66,12,45,0.54) 58%, rgba(0,0,0,0.12) 100%)",
          }}
        >
          <Typography
            sx={{
              px: { xs: 1.1, md: "1.2vw" },
              py: { xs: 1, md: "0.72vw" },
              color: "#fff",
              fontWeight: 800,
              fontSize: { xs: "0.9rem", md: "2vw" },
              background:
                "linear-gradient(90deg, rgba(255,46,149,0.9) 0%, rgba(74,10,47,0.24) 100%)",
            }}
          >
            WILL PLAY FOR ❯
          </Typography>
          <Box sx={{ height: "65%", display: "flex", alignItems: "center", px: { xs: 1.1, md: 3 } }}>
            <div className="dot-loader">
              <span className="diamond"></span>
              <span className="dot"></span>
              <span className="diamond"></span>
              <span className="diamond"></span>
            </div>
          </Box>
        </Box>
      );
    }

    if (isLiveBidding) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: "4px", md: "0.4vw" },
            flex: 1,
            height: "100%",
          }}
        >
          <Box
            sx={{
              flex: 0.62,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(90deg, rgba(172,38,102,0.7) 0%, rgba(48,9,34,0.64) 58%, rgba(0,0,0,0.18) 100%)",
              border: "1px solid rgba(73,145,165,0.35)",
            }}
          >
            <Typography
              sx={{
                color: "#ffec00",
                fontWeight: 900,
                fontSize: { xs: "2.15rem", sm: "3.2rem", md: "4.7vw" },
                lineHeight: 1,
              }}
            >
              {bidAmount}
            </Typography>
            <Typography
              sx={{ color: "#ffd430", fontSize: { xs: "0.8rem", md: "0.8vw" } }}
            >
              CURRENT BID
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 0.38,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(90deg, rgba(172,38,102,0.56) 0%, rgba(35,6,24,0.58) 58%, rgba(0,0,0,0.15) 100%)",
              border: "1px solid rgba(73,145,165,0.28)",
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 900,
                fontSize: { xs: "1.55rem", sm: "2rem", md: "3vw" },
                lineHeight: 1,
              }}
            >
              {basePrice}
            </Typography>
            <Typography sx={{ color: "#fff", fontSize: { xs: "0.72rem", md: "0.8vw" } }}>
              BASE PRICE
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          flex: 1,
          mt: { xs: 0.9, md: "0.55vw" },
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "linear-gradient(180deg, rgba(254, 63, 132, 0.25) 0%, rgba(254, 63, 132, 0.07) 89.53%, rgba(254, 63, 132, 0.00) 100%), rgba(8, 35, 134, 0.00)",
          backdropFilter: "blur(2.277442693710327px)",
          position: "relative",
          px: { xs: 0.7, md: "0.68vw" },
          py: { xs: 0.72, md: "0.72vw" },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr" },
            gap: { xs: 0.28, sm: 0.75, md: "0.24vw" },
          }}
        >
          {statItems.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: { xs: 0.42, md: "0.45vw" },
                color: "#fff",
                px: { xs: 0.28, sm: isDenseStats ? 0.7 : 0.9, md: "0.62vw" },
                py: { xs: 0.18, sm: isDenseStats ? 0.32 : 0.45, md: "0.72vw" },
                background:
                  "linear-gradient(90deg, rgba(254, 63, 132, 0.22) 0%, rgba(254, 63, 132, 0.07) 89.53%, rgba(254, 63, 132, 0.00) 100%)",
                border: "1px solid rgba(243, 108, 36, 0.62)",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.38, md: "0.35vw" },
                  minWidth: 0,
                }}
              >
                <img
                  src={item.icon}
                  alt=""
                  style={{
                    width: isDenseStats ? "1.25vw" : "1.7vw",
                    height: isDenseStats ? "1.25vw" : "1.7vw",
                    minWidth: isDenseStats ? "1.25vw" : "1.7vw",
                    minHeight: isDenseStats ? "1.25vw" : "1.7vw",
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: {
                      xs: "0.34rem",
                      sm: isDenseStats ? "0.72rem" : "0.82rem",
                      md: "2.05vw",
                    },
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 500,
                  fontSize: {
                    xs: "0.36rem",
                    sm: isDenseStats ? "0.86rem" : "0.98rem",
                    md: "1.95vw",
                  },
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.55, md: "0.95vw" },
            mt: { xs: 0.55, md: "1.7vw" },
            px: { xs: 0.3, md: "0.15vw" },
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "0.54rem", sm: "1.15rem", md: "3.4vw" },
              lineHeight: 0.95,
              background:
                "radial-gradient(73.95% 52.29% at 49.38% 41.83%, #ECD815 0%, #F58220 70%, #F15A22 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            BASE
            <br />
            PRICE
          </Typography>
          <Box
            sx={{
              width: { xs: "1px", md: "2px" },
              height: { xs: "26px", md: "4.8vw" },
              background:
                "linear-gradient(180deg, rgba(245,158,11,0) 0%, rgba(245,158,11,0.9) 50%, rgba(245,158,11,0) 100%)",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "0.95rem", sm: "2.2rem", md: "7.2vw" },
              lineHeight: 0.95,
              background:
                "radial-gradient(73.95% 52.29% at 49.38% 41.83%, #ECD815 0%, #F58220 70%, #F15A22 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {basePrice}
          </Typography>
        </Box>
      </Box>
    );
  })();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 0.5, md: "0.6vw" },
        minHeight: { xs: "auto", md: "74vh" },
        justifyContent: { xs: "flex-start", md: "center" },
      }}
    >
      <Box
        sx={{
          width: "fit-content",
          mt: { xs: 0.8, md: 0 },
          ml: { xs: 1.2, md: "0.55vw" },
          px: { xs: 1, md: "0.5vw" },
          py: { xs: 0.25, md: "0.18vw" },
          borderRadius: "999px",
          background: "#e51f3e",
          color: "#fff",
          fontSize: { xs: "0.7rem", md: "1vw" },
          fontWeight: 400,
          lineHeight: 1.1,
        }}
      >
        • Live
      </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: { xs: 0.85, md: "1.5vw" },
            p: { xs: 0.45, md: "0.55vw" },
            pt: { xs: 0.25, md: "0.55vw" },
            width: { xs: "100%", md: "98%" },
            maxWidth: "100%",
            boxSizing: "border-box",
            overflow: "visible",
            mx: { xs: 0, md: "auto" },
          }}
        >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: { xs: "0 0 34%", md: "0 0 35%" },
            minWidth: 0,
            alignSelf: { xs: "auto", md: "stretch" },
          }}
        >
          <Box
            sx={{
              height: { xs: 195, sm: 320, md: "33.5vw" },
              minHeight: { md: "33.5vw" },
              backgroundImage: `url(${profile})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              position: "relative",
              overflow: "visible",
            }}
          >
            {showUnder19Tag && (
              <Box
                sx={{
                  position: "absolute",
                  top: { xs: -6, md: "-0.9vw" },
                  right: { xs: -9, md: "-1.8vw" },
                  px: { xs: 0.48, md: "0.75vw" },
                  py: { xs: 0.08, md: "0.16vw" },
                  borderRadius: { xs: "6px", md: "0.4vw" },
                  border: "2px solid rgba(255,255,255,0.96)",
                  background:
                    "linear-gradient(180deg, #FFE777 0%, #FFD332 62%, #F3B31A 100%)",
                  transform: "skewX(-14deg)",
                  transformOrigin: "right top",
                  zIndex: 20,
                }}
              >
                <Typography
                  sx={{
                    transform: "skewX(14deg)",
                    color: "#6a3400",
                    fontWeight: 900,
                    fontSize: { xs: "0.64rem", md: "1.65vw" },
                    lineHeight: 1,
                  }}
                >
                  {"<19"}
                </Typography>
              </Box>
            )}
            <img
              src={getPlayerPhoto(player)}
              alt={fullName}
              style={{
                width: "96%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "top center",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${profile2})`,
                backgroundSize: "cover",
                p: { xs: 0.6, md: "0.45vw" },
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  textAlign: "center",
                  fontSize: { xs: "0.46rem", sm: "0.66rem", md: "1.15vw" },
                }}
              >
                Dob: {dobText}
              </Typography>
              <Typography
                sx={{
                  color: "#fff",
                  textAlign: "center",
                  fontSize: { xs: "0.54rem", sm: "0.76rem", md: "1.45vw" },
                  mt: { xs: 0.4, sm: 0.65 },
                }}
              >
                {role}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 0.5, sm: 0.8 } }}>
                <Typography
                  sx={{
                    color: "#fff",
                    px: { xs: 0.8, sm: 1.4, md: "1vw" },
                    py: { xs: 0.32, sm: 0.4, md: "0.22vw" },
                    background: "linear-gradient(90deg, #ff8e1f 0%, #ff2e95 100%)",
                    fontSize: { xs: "0.4rem", sm: "0.58rem", md: "1vw" },
                    minWidth: { xs: 78, sm: 110, md: "8.2vw" },
                    textAlign: "center",
                  }}
                >
                  {category}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 0.45, md: "0.7vw" },
            flex: "1 1 0",
            minWidth: 0,
            overflow: "hidden",
            mr: { xs: 0, md: "0.4vw" },
            justifyContent: { xs: "flex-start", md: "stretch" },
          }}
        >
          <Box
            sx={{
              p: { xs: 0.45, md: "0.6vw" },
              minWidth: 0,
              boxSizing: "border-box",
              background:
                "linear-gradient(90deg, rgba(50,19,5,0.96) 0%, rgba(110,42,2,0.95) 70%, rgba(227,116,16,0.95) 100%)",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -2,
                height: "2px",
                background: stripe,
              },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 0.7, minWidth: 0 }}>
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 900,
                    fontSize: { xs: "0.62rem", sm: "0.9rem", md: "1.35vw" },
                    lineHeight: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {fullName}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: { xs: "0.44rem", sm: "0.64rem", md: "0.9vw" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {city}
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: { xs: "0.44rem", sm: "0.64rem", md: "0.9vw" },
                  flexShrink: 0,
                }}
              >
                {mcaNo}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: { xs: 145, md: "28vw" }, display: "flex" }}>
            {statusPanel}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
